var _excluded = ["align", "className", "currentPage", "maxButtons", "selectPage", "showFirstLastPageButtons", "size", "totalPages"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import styled, { css } from 'styled-components';
import * as IconSet from "../../../icons/index.js";
import Button from "../../basic/Button/Button.js";
import { DATA_ATTRS } from "../Tabs/constants.js";
var NAV_ARIA_LABELS = {
  previous: 'Navigate to previous page',
  next: 'Navigate to next page'
};
var PaginationAlignments = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end'
};
var ButtonWidths = {
  small: '24px',
  medium: '36px',
  large: '48px'
};
var sharedStyles = align => css(["align-items:flex-start;display:flex;gap:var(--spacing-1);justify-content:", ";"], PaginationAlignments[align]);

/* Adapting styles from TabGroup. That component does not extend Button, but we
do here because the Pagination can be different sizes (and we get that sizing
for free by extending). As a result, there are some resets and unique
selectors/styles when compared to TabGroup. */
var StyledPaginationButton = styled(Button).withConfig({
  componentId: "CL__sc-18hws3e-0"
})(["aspect-ratio:1;padding:0;height:", ";&.hidden{opacity:0;}border:unset;color:var(--text-color-dim);background-color:unset;&:hover,&:focus,&:not(:hover):focus-visible,&:active{box-shadow:unset;opacity:unset;outline:unset;text-decoration:none;color:var(--text-color);}span{font-weight:var(--body-text-normal-font-weight);}&[", "='true'] span{color:var(--text-color);font-weight:var(--body-text-bold-font-weight);}&::before,&::after{background-color:var(--border-color);border-radius:var(--border-radius-round);bottom:0;content:'';height:", ";left:50%;opacity:0;position:absolute;transform:translateX(-50%);transition:opacity var(--hover-transition);width:calc(100% - var(--spacing-1));}&:not(:hover):focus-visible::before{background-color:var(--text-color);opacity:1;}&:not([", "='true'],:active):hover::before{opacity:1;}&:not([", "='true']):active::before{opacity:0.5;}&[", "='true']::after{background-image:linear-gradient(90deg,var(--purple-4),var(--blue-5));opacity:1;}"], _ref => {
  var {
    size
  } = _ref;
  return ButtonWidths[size];
}, DATA_ATTRS.SELECTED, _ref2 => {
  var {
    size
  } = _ref2;
  return size === 'small' ? '2px' : 'calc(var(--spacing-1) / 2)';
}, DATA_ATTRS.SELECTED, DATA_ATTRS.SELECTED, DATA_ATTRS.SELECTED);
var PaginationItem = _ref3 => {
  var {
    currentPage,
    pageLabel,
    pageNumber,
    selectPage,
    size
  } = _ref3;
  var isActive = pageNumber === currentPage;
  return /*#__PURE__*/React.createElement(StyledPaginationButton, {
    [DATA_ATTRS.SELECTED]: isActive,
    "aria-label": "Navigate to page ".concat(pageNumber),
    key: "pagination-item-".concat(pageNumber),
    size: size,
    onClick: () => selectPage(pageNumber),
    variant: "plain"
  }, pageLabel);
};
var StyledPagination = styled.nav.withConfig({
  componentId: "CL__sc-18hws3e-1"
})(["", ";width:100%;"], _ref4 => {
  var {
    align
  } = _ref4;
  return sharedStyles(align);
});

/* Left and right chevron buttons */
var StyledNavButton = styled(Button).withConfig({
  componentId: "CL__sc-18hws3e-2"
})(["height:", ";> span{transition:transform var(--hover-transition);}&[aria-label='", "']:hover > span,&[aria-label='", "']:focus-visible > span{transform:translateX(calc(-1 * var(--spacing-1) / 2));}&[aria-label='", "']:hover > span,&[aria-label='", "']:focus-visible > span{transform:translateX(calc(var(--spacing-1) / 2));}"], _ref5 => {
  var {
    size
  } = _ref5;
  return ButtonWidths[size];
}, NAV_ARIA_LABELS.previous, NAV_ARIA_LABELS.previous, NAV_ARIA_LABELS.next, NAV_ARIA_LABELS.next);
var StyledButtonsWrapper = styled.div.withConfig({
  componentId: "CL__sc-18hws3e-3"
})(["", ";flex-wrap:wrap;"], _ref6 => {
  var {
    align
  } = _ref6;
  return sharedStyles(align);
});
/**
 * Calculates the basic range of page numbers to be shown. It tries to keep the
 * 'currentPage' in the middle and surround it with an equal number of pages on
 * both sides, but when that's not possible it adds more page numbers to one
 * side to show as many buttons as 'maxButtons' allows
 */
var calculateBasicPageNumbers = _ref7 => {
  var {
    currentPage,
    maxButtons,
    totalPages
  } = _ref7;
  var maxPlacesToTheLeft = Math.floor((maxButtons - 1) / 2);
  var maxPlacesToTheRight = Math.ceil((maxButtons - 1) / 2);

  // Sometimes the current page has few (or none) pages to one side...
  var placesToTheLeft = currentPage > maxPlacesToTheLeft ? maxPlacesToTheLeft : currentPage - 1;
  var placesToTheRight = totalPages - currentPage >= maxPlacesToTheRight ? maxPlacesToTheRight : totalPages - currentPage;

  // ...which means there are unused places
  var unusedPlacesOnTheLeft = maxPlacesToTheLeft - placesToTheLeft;
  var unusedPlacesOnTheRight = maxPlacesToTheRight - placesToTheRight;

  // The opposite side could use those unused places
  var extraPlacesNeededOnTheLeft = currentPage - 1 - placesToTheLeft;
  var extraPlacesNeededOnTheRight = totalPages - currentPage - placesToTheRight;

  // So we transfer unused places on one side to be used on the opposite side
  var totalPlacesToTheLeft = placesToTheLeft + Math.min(unusedPlacesOnTheRight, extraPlacesNeededOnTheLeft);
  var totalPlacesToTheRight = placesToTheRight + Math.min(unusedPlacesOnTheLeft, extraPlacesNeededOnTheRight);
  var totalPlaces = totalPlacesToTheLeft + totalPlacesToTheRight + 1;
  var firstPage = currentPage - totalPlacesToTheLeft;

  // Create the return value, which is the range of page numbers starting with 'firstPage'
  var basicPageNumbers = Array.from(new Array(totalPlaces), (_, i) => i + firstPage);
  return basicPageNumbers;
};
/**
 * When the range of page numbers is far away from the beginning and/or end of
 * the total number of pages, we replace the page numbers at the ends with
 * '1' and 'n' ('n' being the last page number).
 */
var replaceFirstAndLastPageNumbers = _ref8 => {
  var {
    basicPageNumbers,
    totalPages
  } = _ref8;
  var pageNumbers = [...basicPageNumbers];
  if (basicPageNumbers.length < 5) return pageNumbers; // It only makes sense to replace the first/last pages if there are at least 5 buttons

  if (pageNumbers[0] !== 1) pageNumbers[0] = 1;
  if (pageNumbers[pageNumbers.length - 1] !== totalPages) pageNumbers[pageNumbers.length - 1] = totalPages;
  return pageNumbers;
};
/**
 * Calculates the range of page numbers to be shown, with the page numbers at
 * the ends replaced with '1' and 'n' ('n' being the last page number), if needed
 */
var calculatePageNumbers = _ref9 => {
  var {
    currentPage,
    maxButtons,
    showFirstLastPageButtons,
    totalPages
  } = _ref9;
  var basicPageNumbers = calculateBasicPageNumbers({
    currentPage,
    maxButtons,
    totalPages
  });
  if (!showFirstLastPageButtons) return basicPageNumbers;
  var pageNumbers = replaceFirstAndLastPageNumbers({
    basicPageNumbers,
    totalPages
  });
  return pageNumbers;
};
var getPageLabel = (pageIndex, pageNumbers) => {
  var pageNumber = pageNumbers[pageIndex];
  var label = pageNumber.toString();
  var previousPageNumber = pageNumbers[pageIndex - 1];
  var nextPageNumber = pageNumbers[pageIndex + 1];
  var lastPageNumber = pageNumbers[pageNumbers.length - 1];
  if (pageNumber === 1 && nextPageNumber !== 2) label = '1...';
  if (pageNumber === lastPageNumber && previousPageNumber !== lastPageNumber - 1) label = "...".concat(pageNumber);
  return label;
};
var Pagination = _ref0 => {
  var {
      align = 'center',
      className,
      currentPage,
      maxButtons = 9,
      selectPage,
      showFirstLastPageButtons = false,
      size = 'medium',
      totalPages
    } = _ref0,
    props = _objectWithoutProperties(_ref0, _excluded);
  var pageNumbers = calculatePageNumbers({
    currentPage,
    maxButtons,
    showFirstLastPageButtons,
    totalPages
  });
  return /*#__PURE__*/React.createElement(StyledPagination, _extends({
    align: align,
    "aria-label": "Pagination",
    className: className
  }, props), /*#__PURE__*/React.createElement(StyledNavButton, {
    "aria-label": NAV_ARIA_LABELS.previous,
    disabled: currentPage === 1,
    icon: IconSet.ChevronLeftLarge,
    monochrome: true,
    onClick: () => selectPage(currentPage - 1),
    size: size,
    variant: "plain"
  }), /*#__PURE__*/React.createElement(StyledButtonsWrapper, {
    align: align
  }, pageNumbers.map((pageNumber, pageIndex) => /*#__PURE__*/React.createElement(PaginationItem, {
    currentPage: currentPage,
    key: pageNumber,
    pageLabel: getPageLabel(pageIndex, pageNumbers),
    pageNumber: pageNumber,
    selectPage: selectPage,
    size: size
  }))), /*#__PURE__*/React.createElement(StyledNavButton, {
    "aria-label": NAV_ARIA_LABELS.next,
    disabled: currentPage === totalPages,
    icon: IconSet.ChevronRightLarge,
    monochrome: true,
    onClick: () => selectPage(currentPage + 1),
    size: size,
    variant: "plain"
  }));
};
export default Pagination;